import * as SchemaFieldConstants from 'schemas/fields/SchemaFieldConstants';
import {SchemaFieldSelector} from 'schemas/SchemaFieldSelectorConstants';

import {TagSchemaSummaryGraphQL} from '../../../graphql-fragments/schemas/tag-schemas/__generated/TagSchemaSummaryGraphQL';
import {FieldTableQuery_schemaFieldSelectors_SchemaFieldSelectorNode as FieldTableQuerySelector} from './__generated/FieldTableQuery';
import {ClientSchemaField} from './FieldTable';

function isGraphQLSelector(
  selector: FieldTableQuerySelector | SchemaFieldSelector
): selector is FieldTableQuerySelector {
  return (selector as FieldTableQuerySelector).registry != null;
}

export interface SchemaLinkValidationContext {
  tagSchemasInOrg?: Array<TagSchemaSummaryGraphQL>;
  // Schema field selectors (dropdowns) available for use in this context - typically includes selectors from current org
  // plus any legacy cross-org selectors that are already used in existing fields
  availableSchemaFieldSelectors?: Array<FieldTableQuerySelector> | Array<SchemaFieldSelector>;
  ownerId: string;
  registryId?: string;
}

/**
 * Checks if a field's linked schema or dropdown belongs to the current organization.
 * This prevents creating broken links to schemas/dropdowns from other organizations.
 *
 * For entity link fields (BIOENTITY_LINK, PART_LINK, etc.), we check if the target schema
 * exists in tagSchemasInOrg
 *
 * For dropdown fields (SELECTOR), we check if the dropdown's registry organization matches
 * the current ownerId. Note: schemaFieldSelectors may include legacy cross-org dropdowns
 * already in use, but we only validate when registry info is available (GraphQL type).
 *
 * For storable link fields (STORABLE_LINK), we check if the storable schema's registryId
 * matches the current registryId.
 *
 * If we cannot validate, we default to allowing the link.
 *
 * We did allow linking to schemas from other organizations in the past, but we don't anymore.
 * We don't link to those schemas because that would require fetching registry info about each org.
 */
export function shouldEnableSchemaLinkForField(
  field: ClientSchemaField,
  context: SchemaLinkValidationContext
): boolean {
  if (!context.tagSchemasInOrg) {
    return false;
  }

  if (field.fieldType === SchemaFieldConstants.FieldType.SELECTOR && field.schemaFieldSelectorId) {
    const selector = context.availableSchemaFieldSelectors?.find((s) => s.id === field.schemaFieldSelectorId);
    // Only validate if we have registry info (GraphQL type). If REST type, we can't validate since we don't have that info
    if (selector && isGraphQLSelector(selector)) {
      if (selector.registry?.organization?.id !== context.ownerId) {
        return false;
      }
    }
  }

  if (
    field.fieldType === SchemaFieldConstants.FieldType.STORABLE_LINK &&
    field.requiredLink &&
    SchemaFieldConstants.isRequiredLinkStorableSchema(field.requiredLink)
  ) {
    const storableSchema = field.requiredLink.storableSchema;

    if (storableSchema.registryId && context.registryId && storableSchema.registryId !== context.registryId) {
      return false;
    }
  }

  const linkedTagSchemaId =
    field.requiredLink && SchemaFieldConstants.isRequiredLinkTagSchema(field.requiredLink)
      ? field.requiredLink.tagSchema?.id
      : undefined;

  if (!linkedTagSchemaId) {
    return true;
  }

  const schemaExistsInListing = context.tagSchemasInOrg.some((schema) => schema.id === linkedTagSchemaId);

  return schemaExistsInListing;
}
